import { fetchData } from 'Utils/fetch-data';
import type { FetchResponseI } from 'types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface JobTitleCoursesKpisI {
  /** Puestos distintos que aparecen bajo los filtros aplicados. */
  totalJobTitles: number;
  /** Cursos DISTINTOS: un curso definido en varios puestos cuenta una sola vez. */
  totalCourses: number;
  /**
   * Conteo con repeticiones (una por asignación curso-puesto). Se calcula
   * siempre; si el negocio decide que el indicador debe contar repetidos,
   * basta con mostrar este campo en lugar de `totalCourses`.
   */
  totalAssignments: number;
  totalTrainingHours: number;
  /**
   * Cursos distintos que SÍ tienen horas capturadas. Cuando es 0, la suma de
   * horas es 0 por falta de dato, no porque los cursos duren cero.
   */
  coursesWithHours: number;
}

export interface JobTitleCourseRowI {
  /** Id de la asignación (`CourseAreas.id`), único por fila. */
  id: string;
  courseId: string;
  courseName: string;
  categoryId: string | null;
  categoryName: string | null;
  /** `null` cuando el curso no tiene horas capturadas (distinto de 0). */
  trainingHours: number | null;
}

export interface JobTitleCoursesGroupI {
  /** Id del puesto. */
  groupKey: string;
  /** Nombre del puesto, ya listo para mostrar. */
  groupLabel: string;
  groupMeta: { coursesCount: number; totalTrainingHours: number };
  courses: JobTitleCourseRowI[];
}

export interface JobTitleCoursesReportResponseI {
  kpis: JobTitleCoursesKpisI;
  groups: JobTitleCoursesGroupI[];
}

export interface JobTitleCoursesReportFiltersI {
  page?: number;
  limit?: number;
  search?: string;
  jobTitleIds?: string[];
  categoryIds?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API client
// ─────────────────────────────────────────────────────────────────────────────

class JobTitleCoursesReportApi {
  private readonly endpoint = '/reports/job-titles/courses';

  async fetchReport(
    filters: JobTitleCoursesReportFiltersI = {}
  ): Promise<FetchResponseI<JobTitleCoursesReportResponseI>> {
    return fetchData<JobTitleCoursesReportResponseI>(this.endpoint, {
      method: 'GET',
      params: filters,
      includePagination: true,
    });
  }

  async exportReport(filters: JobTitleCoursesReportFiltersI = {}): Promise<FetchResponseI<Blob>> {
    return fetchData<Blob>(`${this.endpoint}/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
  }
}

const jobTitleCoursesReportApi = new JobTitleCoursesReportApi();
export default jobTitleCoursesReportApi;
